import { useEffect, useState } from "react";
import { sortByLabel } from "@/lib/sortRows";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import { StatusBadge, STATUS_STYLES } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PagedList from "@/components/PagedList";
import PeopleMultiSelect from "@/components/PeopleMultiSelect";
import { initiativeOwnerOptions } from "@/lib/initiativeOwners";
import { sortByName } from "@/lib/sortRows";
import { PlusIcon, ArrowRightIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUSES = ["planning", "active", "on_hold", "completed"];

export default function InitiativesPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  // owner_ids defaults to the creator: the common case is "I own what I just
  // created", and an initiative with no owner at all is not useful to anyone.
  // NOT pre-filled with the creator any more: ownership is restricted to a
  // fixed group (lib/initiativeOwners.js), and the person creating an
  // initiative is usually not in it.
  const emptyForm = () => ({ title: "", description: "", status: "active", owner_ids: [] });
  const [form, setForm] = useState(emptyForm());

  const load = async () => {
    const [i, u] = await Promise.all([api.get("/initiatives"), api.get("/users")]);
    setItems(i.data);
    setUsers(sortByName(u.data));
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    try {
      // Explicit, even though the DB now also defaults owner_id to
      // auth.uid() on insert (belt-and-suspenders — see
      // supabase/schema_security_fixes.sql's owner_id-default follow-up).
      // owner_id stays the creator — it is the id every initiatives RLS policy
      // tests against, so it is not the multi-owner field. owner_ids is.
      await api.post("/initiatives", { ...form, owner_id: user?.id });
      toast.success("Initiative created");
      setForm(emptyForm());
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const uById = Object.fromEntries(users.map((u) => [u._id, u.name || u.email]));
  // Falls back to owner_id for a row created before owner_ids existed and never
  // edited since (the migration backfills, but a fresh insert from an older
  // build wouldn't be covered).
  const ownerNames = (i) => {
    const ids = (i.owner_ids || []).length > 0 ? i.owner_ids : [i.owner_id].filter(Boolean);
    return ids.map((id) => uById[id] || "Unknown");
  };

  return (
    <AppLayout>
      <div className="trk-sticky-header sticky top-0 z-20 px-8 py-4 flex items-center justify-between">
        <div>
          <div className="trk-uppercase-label">Portfolio</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Initiatives</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-initiative-button" className="rounded-sm bg-[#0176D3] hover:bg-[#005FB2]">
              <PlusIcon size={16} weight="bold" className="mr-1" /> New Initiative
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm max-w-lg">
            <DialogHeader><DialogTitle className="font-display">Create Initiative</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="trk-uppercase-label mb-2 block">Title <span className="text-red-600">*</span></Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="initiative-title-input" className="rounded-sm" />
              </div>
              <div>
                <Label className="trk-uppercase-label mb-2 block">Description</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="initiative-description-input" className="rounded-sm" rows={3} />
              </div>
              <div>
                <Label className="trk-uppercase-label mb-2 block">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="initiative-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {sortByLabel(STATUSES, (s) => STATUS_STYLES[s]?.label || s).map((s) => <SelectItem key={s} value={s}>{STATUS_STYLES[s]?.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="trk-uppercase-label mb-2 block">
                  Owner{form.owner_ids.length > 1 ? "s" : ""} {form.owner_ids.length > 0 && `(${form.owner_ids.length})`}
                </Label>
                {/* Only the four accounts allowed to own an initiative —
                    see lib/initiativeOwners.js for the list and why it is
                    matched on the email local-part. */}
                <PeopleMultiSelect
                  users={initiativeOwnerOptions(users, form.owner_ids)}
                  value={form.owner_ids}
                  onChange={(ids) => setForm({ ...form, owner_ids: ids })}
                  triggerPlaceholder="Select owner(s)…"
                  emptyMessage="None of the accounts allowed to own an initiative exist yet."
                  testId="initiative-owners"
                />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} data-testid="save-initiative-button" className="rounded-sm bg-[#0176D3] hover:bg-[#005FB2]">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="px-8 py-6 trk-fade-in">
        {items.length === 0 ? (
          <div className="border border-dashed border-border rounded-sm p-12 text-center">
            <div className="trk-uppercase-label mb-2">Empty</div>
            <h3 className="font-display text-xl font-bold mb-2">No initiatives yet</h3>
            <p className="text-sm text-zinc-500 mb-4">Create your first initiative to group related projects.</p>
            <Button onClick={() => setOpen(true)} className="rounded-sm bg-[#0176D3] hover:bg-[#005FB2]">
              <PlusIcon size={16} weight="bold" className="mr-1" /> New Initiative
            </Button>
          </div>
        ) : (
          <PagedList items={items} pageSize={12} testIdPrefix="initiatives">
            {(rows) => (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {rows.map((i) => (
              <Link
                key={i._id}
                to={`/initiatives/${i._id}`}
                data-testid={`initiative-card-${i._id}`}
                className="trk-card trk-hover-lift p-5 flex flex-col justify-between min-h-[160px]"
              >
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <StatusBadge status={i.status} />
                    <ArrowRightIcon size={16} className="text-zinc-400" />
                  </div>
                  <h3 className="font-display text-lg font-bold tracking-tight leading-tight">{i.title}</h3>
                  <p className="text-sm text-zinc-500 mt-2 line-clamp-2">{i.description || "—"}</p>
                </div>
                <div className="mt-4">
                  <div className="trk-uppercase-label">
                    {ownerNames(i).length > 1 ? "Owners" : "Owner"}
                  </div>
                  <div className="text-sm text-zinc-700 truncate" title={ownerNames(i).join(", ")}>
                    {ownerNames(i).length > 0 ? ownerNames(i).join(", ") : "—"}
                  </div>
                  <div className="trk-uppercase-label mt-2">
                    Created {i.created_at ? new Date(i.created_at).toLocaleDateString() : ""}
                  </div>
                </div>
              </Link>
            ))}
              </div>
            )}
          </PagedList>
        )}
      </div>
    </AppLayout>
  );
}
